import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Volume2, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Progress } from '../../components/ui/Progress';
import { Slider } from '../../components/ui/Slider';
import { ScrollArea } from '../../components/ui/ScrollArea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/Tooltip';
import { riveFiles, riveCollections } from '../../src/seed';
export function RiveFileDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const file = riveFiles.find(f => f.id === id);
  if (!file) {
    return <Card data-id="element-622">
        <CardHeader data-id="element-623">
          <CardTitle data-id="element-624">File not found</CardTitle>
        </CardHeader>
      </Card>;
  }
  const fileCollections = riveCollections.filter(c => file.collectionIds.includes(c.id));
  const useCaseLabel = file.useCase === 'yoga' ? 'Yoga sequence' : file.useCase === 'viseme' ? 'Viseme mouth sync' : 'Illustration';
  const yogaTranscript = [{
    time: '0:00',
    text: 'Begin in Mountain Pose, standing tall with feet together and arms at your sides.'
  }, {
    time: '0:15',
    text: 'Inhale, sweep your arms up overhead, bringing your palms together.'
  }, {
    time: '0:30',
    text: 'Exhale, hinge at the hips and fold forward into Forward Fold.'
  }, {
    time: '0:45',
    text: 'Inhale, lift halfway up, lengthening your spine.'
  }, {
    time: '1:00',
    text: 'Exhale, plant your hands and step back into Plank Pose.',
    active: true
  }, {
    time: '1:15',
    text: 'Lower down slowly through Chaturanga.'
  }, {
    time: '1:30',
    text: 'Inhale, lift your chest into Upward Facing Dog.'
  }, {
    time: '1:45',
    text: 'Exhale, press back into Downward Facing Dog.'
  }];
  const visemeTranscript = [{
    time: '0:00',
    text: 'Hello, I will be guiding you through your post-operative care instructions.'
  }, {
    time: '0:15',
    text: 'It is very important that you take your prescribed antibiotics exactly as directed.'
  }, {
    time: '0:30',
    text: 'Do not stop taking them even if you start feeling better.'
  }, {
    time: '0:45',
    text: 'For pain management, you may take ibuprofen every 6 to 8 hours.'
  }, {
    time: '1:00',
    text: 'Keep the surgical site clean and dry for the first 48 hours.',
    active: true
  }, {
    time: '1:15',
    text: 'You may shower after 48 hours, but do not submerge the incision in water.'
  }, {
    time: '1:30',
    text: 'Watch for signs of infection such as increased redness, swelling, or fever.'
  }, {
    time: '1:45',
    text: 'If you experience any of these symptoms, contact our office immediately.'
  }];
  const transcript = file.useCase === 'yoga' ? yogaTranscript : visemeTranscript;
  return <div className="space-y-6 pb-10" data-id="element-625">
      <div data-id="element-626">
        <Button variant="ghost" size="sm" className="-ml-3 mb-2 text-muted-foreground" onClick={() => navigate(-1)} data-id="element-627">
          <ArrowLeft className="mr-2 h-4 w-4" data-id="element-628" />
          Back
        </Button>
        <h1 className="text-3xl font-bold tracking-tight mb-3" data-id="element-629">{file.name}</h1>
        <div className="flex flex-wrap gap-2" data-id="element-630">
          {fileCollections.map(c => <Badge key={c.id} variant="outline" data-id="element-631">
              {c.name}
            </Badge>)}
          <Badge variant="secondary" data-id="element-632">{useCaseLabel}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-633">
        <div className="lg:col-span-2 space-y-6" data-id="element-634">
          <Card className="overflow-hidden bg-black text-white h-80 flex items-center justify-center relative" data-id="element-635">
            <Button variant="ghost" size="icon" className="h-20 w-20 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={() => setIsPlaying(!isPlaying)} data-id="element-636">
              {isPlaying ? <Pause className="h-10 w-10" data-id="element-637" /> : <Play className="h-10 w-10 ml-2" data-id="element-638" />}
            </Button>
          </Card>

          <Card data-id="element-639">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-640">
              <Button size="icon" variant="ghost" className="rounded-full h-10 w-10 shrink-0" onClick={() => setIsPlaying(!isPlaying)} data-id="element-641">
                {isPlaying ? <Pause className="h-5 w-5" data-id="element-642" /> : <Play className="h-5 w-5 ml-1" data-id="element-643" />}
              </Button>
              <div className="flex-1 space-y-2" data-id="element-644">
                <Progress value={isPlaying ? 45 : 0} className="h-2" data-id="element-645" />
                <div className="flex justify-between text-xs text-muted-foreground font-medium" data-id="element-646">
                  <span data-id="element-647">{isPlaying ? '1:00' : '0:00'}</span>
                  <span data-id="element-648">2:30</span>
                </div>
              </div>
              <div className="w-24 shrink-0 flex items-center gap-2" data-id="element-649">
                <Volume2 className="h-4 w-4 text-muted-foreground" data-id="element-650" />
                <Slider defaultValue={[80]} max={100} step={1} data-id="element-651" />
              </div>
            </CardContent>
          </Card>

          <div data-id="element-652">
            <Button size="lg" className="w-full text-lg h-14" data-id="element-653">
              Open Rive editor
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2" data-id="element-654">
              Edit timeline events, state-machine activation, and audio sync in
              a dedicated editor — coming in the Rive feature brainstorm.
            </p>
          </div>

          <Card data-id="element-655">
            <CardContent className="p-6" data-id="element-656">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-8 text-sm" data-id="element-657">
                <div data-id="element-658">
                  <p className="text-muted-foreground mb-1" data-id="element-659">File Size</p>
                  <p className="font-medium" data-id="element-660">{file.sizeKb} KB</p>
                </div>
                <div data-id="element-661">
                  <p className="text-muted-foreground mb-1" data-id="element-662">Events</p>
                  <p className="font-medium" data-id="element-663">{file.eventCount}</p>
                </div>
                <div data-id="element-664">
                  <p className="text-muted-foreground mb-1" data-id="element-665">Use Case</p>
                  <p className="font-medium capitalize" data-id="element-666">{file.useCase}</p>
                </div>
                <div data-id="element-667">
                  <p className="text-muted-foreground mb-1" data-id="element-668">Audio Duration</p>
                  <p className="font-medium" data-id="element-669">2:30</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-670">
            <CardHeader className="pb-3" data-id="element-671">
              <CardTitle className="text-sm font-medium text-muted-foreground" data-id="element-672">
                Sparse density (yoga)
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-673">
              <div className="relative h-12 flex items-center" data-id="element-674">
                <div className="absolute left-0 right-0 h-1 bg-muted rounded-full" data-id="element-675" />
                <TooltipProvider data-id="element-676">
                  <Tooltip data-id="element-677">
                    <TooltipTrigger asChild data-id="element-678">
                      <div className="absolute left-[10%] -mt-1.5 h-4 w-4 rounded-full bg-primary cursor-pointer hover:scale-125 transition-transform" data-id="element-679" />
                    </TooltipTrigger>
                    <TooltipContent data-id="element-680">Pose 1: Mountain Pose</TooltipContent>
                  </Tooltip>
                  <Tooltip data-id="element-681">
                    <TooltipTrigger asChild data-id="element-682">
                      <div className="absolute left-[25%] -mt-1.5 h-4 w-4 rounded-full bg-orange-500 cursor-pointer hover:scale-125 transition-transform" data-id="element-683" />
                    </TooltipTrigger>
                    <TooltipContent data-id="element-684">Pose 2: Forward Fold</TooltipContent>
                  </Tooltip>
                  <Tooltip data-id="element-685">
                    <TooltipTrigger asChild data-id="element-686">
                      <div className="absolute left-[45%] -mt-1.5 h-4 w-4 rounded-full bg-emerald-500 cursor-pointer hover:scale-125 transition-transform" data-id="element-687" />
                    </TooltipTrigger>
                    <TooltipContent data-id="element-688">Pose 3: Downward dog</TooltipContent>
                  </Tooltip>
                  <div className="absolute left-[55%] -mt-1.5 h-4 w-4 rounded-full bg-primary cursor-pointer hover:scale-125 transition-transform" data-id="element-689" />
                  <div className="absolute left-[65%] -mt-1.5 h-4 w-4 rounded-full bg-blue-500 cursor-pointer hover:scale-125 transition-transform" data-id="element-690" />
                  <div className="absolute left-[80%] -mt-1.5 h-4 w-4 rounded-full bg-orange-500 cursor-pointer hover:scale-125 transition-transform" data-id="element-691" />
                  <div className="absolute left-[90%] -mt-1.5 h-4 w-4 rounded-full bg-emerald-500 cursor-pointer hover:scale-125 transition-transform" data-id="element-692" />
                  <div className="absolute left-[95%] -mt-1.5 h-4 w-4 rounded-full bg-primary cursor-pointer hover:scale-125 transition-transform" data-id="element-693" />
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-694">
            <CardHeader className="pb-3" data-id="element-695">
              <CardTitle className="text-sm font-medium text-muted-foreground" data-id="element-696">
                Dense density (viseme)
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-697">
              <div className="relative h-12 flex items-center" data-id="element-698">
                <div className="absolute left-0 right-0 h-1 bg-muted rounded-full" data-id="element-699" />
                <div className="absolute left-0 right-0 flex justify-between px-1" data-id="element-700">
                  {Array.from({
                  length: 60
                }).map((_, i) => <div key={i} className="h-3 w-0.5 bg-primary/60 rounded-full" style={{
                  opacity: Math.random() * 0.5 + 0.5,
                  transform: `scaleY(${Math.random() * 0.8 + 0.6})`
                }} data-id="element-701" />)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-702">
          <Card className="h-full flex flex-col" data-id="element-703">
            <CardHeader data-id="element-704">
              <CardTitle data-id="element-705">Transcript</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-0" data-id="element-706">
              <ScrollArea className="h-[600px] px-6 pb-6" data-id="element-707">
                <div className="space-y-4" data-id="element-708">
                  {transcript.map((item, i) => <div key={i} className={`flex gap-3 ${item.active ? 'bg-accent/50 p-2 -mx-2 rounded-md' : ''}`} data-id="element-709">
                      <span className={`text-xs font-mono w-12 shrink-0 pt-0.5 ${item.active ? 'text-accent-foreground/70' : 'text-muted-foreground'}`} data-id="element-710">
                        {item.time}
                      </span>
                      <p className={`text-sm leading-relaxed ${item.active ? 'font-medium' : ''}`} data-id="element-711">
                        {item.text}
                      </p>
                    </div>)}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="w-full" data-id="element-712">
        <CardHeader data-id="element-713">
          <CardTitle data-id="element-714">Tools</CardTitle>
        </CardHeader>
        <CardContent data-id="element-715">
          <Tabs defaultValue="editor" data-id="element-716">
            <TabsList data-id="element-717">
              <TabsTrigger value="editor" data-id="element-718">Rive editor</TabsTrigger>
            </TabsList>
            <div className="mt-6" data-id="element-719">
              <TabsContent value="editor" data-id="element-720">
                <div className="flex items-start gap-3 rounded-lg border p-4" data-id="element-721">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" data-id="element-722" />
                  <p className="text-sm text-muted-foreground" data-id="element-723">
                    The Rive editor — timeline event placement, state-machine
                    activation, audio-transcript sync — is designed separately
                    as part of the Rive feature brainstorm. This detail view
                    shows the shell composition only.
                  </p>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>;
}